import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowRight, FiUsers } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { tourService } from '../services/tourService';
import { ErrorState, LoadingState, PageHero, SectionHeading } from '../components/ui/PageElements';
import { formatCurrency } from '../utils/formatters';

const TourReservationPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    numberOfPeople: 1,
    specialRequests: ''
  });

  useEffect(() => {
    const fetchTour = async () => {
      try {
        setLoading(true);
        const data = await tourService.getTourById(id);
        setTour(data);
        setError('');
      } catch (requestError) {
        console.error('Tour reservation error:', requestError);
        setError(t('design.tourReservation.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchTour();
  }, [id, t]);

  const total = useMemo(() => Number(tour?.price || 0) * Number(formData.numberOfPeople || 0), [formData.numberOfPeople, tour?.price]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    alert(t('design.tourReservation.alerts.submitted'));
    navigate('/');
  };

  if (loading) {
    return (
      <Layout>
        <LoadingState label={t('design.tourReservation.loading')} />
      </Layout>
    );
  }

  if (error || !tour) {
    return (
      <Layout>
        <ErrorState title={t('design.tourReservation.unavailableTitle')} message={error || t('design.tourReservation.notFound')} />
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHero
        eyebrow={t('design.tourReservation.heroEyebrow')}
        title={t('design.tourReservation.heroTitle', { name: tour.tourName })}
        description={t('design.tourReservation.heroDescription')}
        aside={
          <div className="soft-panel p-6 sm:p-8">
            <p className="eyebrow">{t('design.tourReservation.summaryEyebrow')}</p>
            <div className="mt-6 space-y-4">
              <div className="rounded-[24px] border border-stone-200 bg-stone-50/80 p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500">{t('design.tourReservation.perGuest')}</p>
                <p className="mt-2 font-serif text-3xl font-semibold text-slate-900">{formatCurrency(tour.price)}</p>
              </div>
              <div className="rounded-[24px] border border-stone-200 bg-stone-50/80 p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500">{t('design.tourReservation.currentTotal')}</p>
                <p className="mt-2 font-serif text-4xl font-semibold text-slate-900">{formatCurrency(total)}</p>
              </div>
            </div>
          </div>
        }
      />

      <section className="content-width pb-12">
        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="soft-panel p-6 sm:p-8">
            <SectionHeading eyebrow={t('design.tourReservation.formEyebrow')} title={t('design.tourReservation.formTitle')} description={t('design.tourReservation.formDescription')} />
            <form onSubmit={handleSubmit} className="grid gap-5">
              <div className="grid gap-5 md:grid-cols-2">
                <div>
                  <label className="field-label">{t('hotels.reservation.fullName')}</label>
                  <input name="fullName" value={formData.fullName} onChange={handleInputChange} className="input-shell" required />
                </div>
                <div>
                  <label className="field-label">{t('hotels.reservation.email')}</label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="input-shell" required />
                </div>
                <div>
                  <label className="field-label">{t('hotels.reservation.phone')}</label>
                  <input name="phone" value={formData.phone} onChange={handleInputChange} className="input-shell" required />
                </div>
                <div>
                  <label className="field-label">{t('tours.reservation.participants')}</label>
                  <div className="relative">
                    <FiUsers className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="number"
                      min="1"
                      name="numberOfPeople"
                      value={formData.numberOfPeople}
                      onChange={handleInputChange}
                      className="input-shell pl-11"
                      required
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="field-label">{t('hotels.reservation.specialRequests')}</label>
                <textarea
                  name="specialRequests"
                  value={formData.specialRequests}
                  onChange={handleInputChange}
                  rows="5"
                  className="input-shell"
                  placeholder={t('design.tourReservation.specialRequestsPlaceholder')}
                />
              </div>

              <div className="flex flex-col gap-4 rounded-[28px] border border-stone-200 bg-stone-50/80 p-5 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-stone-500">{t('design.tourReservation.estimatedTotal')}</p>
                  <p className="mt-2 font-serif text-4xl font-semibold text-slate-900">{formatCurrency(total)}</p>
                </div>
                <button type="submit" className="btn-primary">
                  {t('design.tourReservation.submit')}
                  <FiArrowRight className="h-4 w-4" />
                </button>
              </div>
            </form>
          </div>

          <div className="soft-panel p-6 sm:p-8">
            <p className="eyebrow">{t('design.tourReservation.routeSummary')}</p>
            <div className="mt-5 space-y-4">
              <div className="rounded-[24px] border border-stone-200 bg-stone-50/80 p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500">{t('design.tourReservation.tourLabel')}</p>
                <p className="mt-2 text-lg font-semibold text-slate-900">{tour.tourName}</p>
              </div>
              <div className="rounded-[24px] border border-stone-200 bg-stone-50/80 p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500">{t('design.tourReservation.routeLabel')}</p>
                <p className="mt-2 text-sm leading-7 text-slate-700">{t('design.tourReservation.routeValue', { start: tour.tourStartAddress, end: tour.tourEndAddress })}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TourReservationPage;
